import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const SERVICES = [
  "Endüstriyel Elektrik", "Elektrik Taahhüt", "Otomasyon",
  "Pano Sistemleri", "Projelendirme", "Bakım / Teknik Servis", "Diğer",
];

export function ContactForm({ dark = false }) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = new URLSearchParams({
        "form-name": "contact",
        name: values.name,
        company: values.company || "",
        phone: values.phone || "",
        email: values.email,
        service_type: values.service_type || "",
        message: values.message,
        "bot-field": values["bot-field"] || "",
      });

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      if (!response.ok) throw new Error("Form submission failed");
      toast.success("Talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.");
      reset();
    } catch {
      toast.error("Talebiniz gönderilemedi. Lütfen daha sonra tekrar deneyin veya doğrudan isa@sistemmuhendislik.com adresine yazın.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `block text-xs font-semibold tracking-wide uppercase mb-2 ${dark ? "text-silver/70" : "text-ink/60"}`;
  const inputCls = `w-full bg-transparent border px-4 py-3 text-sm outline-none transition-colors duration-300 ${
    dark ? "border-white/15 text-white placeholder-silver/40 focus:border-cyan"
         : "border-ink/15 text-ink placeholder-ink/30 focus:border-tech"
  }`;
  const errCls = "text-red-500 text-xs mt-1";

  return (
    <form name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit(onSubmit)} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <input type="hidden" name="form-name" value="contact" />
      <p className="absolute overflow-hidden [clip:rect(0,0,0,0)] h-px w-px -m-px p-0 border-0">
        <label htmlFor="contact-bot-field">Bu alanı boş bırakın: <input id="contact-bot-field" tabIndex={-1} autoComplete="off" {...register("bot-field")} /></label>
      </p>
      <div>
        <label htmlFor="contact-name" className={labelCls}>Ad Soyad *</label>
        <input id="contact-name" autoComplete="name" data-testid="contact-name" className={inputCls} placeholder="Adınız Soyadınız"
          {...register("name", { required: "Ad soyad zorunludur" })} />
        {errors.name && <p className={errCls}>{errors.name.message}</p>}
      </div>
      <div>
        <label htmlFor="contact-company" className={labelCls}>Firma</label>
        <input id="contact-company" autoComplete="organization" data-testid="contact-company" className={inputCls} placeholder="Firma adı" {...register("company")} />
      </div>
      <div>
        <label htmlFor="contact-phone" className={labelCls}>Telefon</label>
        <input id="contact-phone" type="tel" autoComplete="tel" data-testid="contact-phone" className={inputCls} placeholder="+90 ..."
          {...register("phone")} />
      </div>
      <div>
        <label htmlFor="contact-email" className={labelCls}>E-posta *</label>
        <input id="contact-email" autoComplete="email" data-testid="contact-email" type="email" className={inputCls} placeholder="ornek@firma.com"
          {...register("email", { required: "E-posta zorunludur", pattern: { value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, message: "Geçerli bir e-posta girin" } })} />
        {errors.email && <p className={errCls}>{errors.email.message}</p>}
      </div>
      <div className="md:col-span-2">
        <label htmlFor="contact-service" className={labelCls}>Hizmet Türü</label>
        <select id="contact-service" data-testid="contact-service" className={`${inputCls} ${dark ? "[&>option]:text-ink" : ""}`} defaultValue="" {...register("service_type")}>
          <option value="" disabled>Seçiniz</option>
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label htmlFor="contact-message" className={labelCls}>Proje Açıklaması *</label>
        <textarea id="contact-message" data-testid="contact-message" rows={5} className={inputCls} placeholder="Projeniz hakkında kısaca bilgi verin..."
          {...register("message", { required: "Proje açıklaması zorunludur" })} />
        {errors.message && <p className={errCls}>{errors.message.message}</p>}
      </div>
      <div className="md:col-span-2">
        <p className={`text-xs leading-relaxed ${dark ? "text-silver/55" : "text-ink/50"}`}>
          Veri sorumlusu <strong className={dark ? "text-silver/80" : "text-ink/70"}>İsa Şeker</strong>'dir. Bu formda paylaştığınız bilgiler talebinizi almak, değerlendirmek, sizinle iletişime geçmek ve gerekli ise teklif sürecini yürütmek amacıyla işlenir; form altyapısı Netlify tarafından sağlanır. Ayrıntılı bilgi için{" "}
          <Link to="/kvkk-aydinlatma-metni" className="text-tech underline underline-offset-2 hover:text-cyan">KVKK Aydınlatma Metni</Link>'ni inceleyebilirsiniz.
        </p>
      </div>
      <div className="md:col-span-2">
        <button
          type="submit"
          data-testid="contact-submit"
          disabled={loading}
          className="group inline-flex items-center justify-center gap-2 bg-cyan hover:bg-tech text-navy hover:text-white font-semibold text-sm px-8 py-4 transition-colors duration-300 clip-tag disabled:opacity-60"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> GÖNDERİLİYOR</> : <>TALEBİ GÖNDER <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
        </button>
      </div>
    </form>
  );
}
